// src/pages/admin/TalukMasterManager.js
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit2,
  Filter,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X,
} from "react-feather";
import { toast } from "react-toastify";

import "../../../css/master-common.css";

import {
  fetchDistrictListThunk,
  fetchTalukListThunk,
  createTalukThunk,
  updateTalukThunk,
  deleteTalukThunk,
  openDtModal,
  closeDtModal,
  setDtForm,
  resetDtForm,
  setTalukFilters,
} from "../../../store/slices/districtTalukMasterSlice";

const e = React.createElement;

const ITEMS_PER_PAGE = 10;

const SORT_OPTIONS = [
  { value: "taluk_name_asc", label: "Taluk Name (A-Z)" },
  { value: "taluk_name_desc", label: "Taluk Name (Z-A)" },
  { value: "dist_name_asc", label: "District Name (A-Z)" },
  { value: "dist_name_desc", label: "District Name (Z-A)" },
  { value: "status_active_first", label: "Status (Active first)" },
  { value: "status_inactive_first", label: "Status (Inactive first)" },
];

const Pill = ({ children }) => e(Badge, { className: "mm-pill" }, children);

const StatusPill = ({ status }) => {
  const isActive = Number(status) === 1;
  return e(Pill, null, isActive ? "Active" : "Inactive");
};

export default function TalukMasterManager() {
  const dispatch = useDispatch();
  const {
    districts,
    taluks,
    talukLoading,
    talukSaving,
    talukDeleting,
    talukFilters,
    modalOpen,
    modalMode,
    modalType,
    form,
    error,
  } = useSelector((s) => s.districtTalukMaster);

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("taluk_name_asc");
  const [currentPage, setCurrentPage] = useState(1);

  // load districts once
  useEffect(() => {
    dispatch(fetchDistrictListThunk());
  }, [dispatch]);

  // load taluks whenever district filter changes
  useEffect(() => {
    dispatch(fetchTalukListThunk({ dist_id: talukFilters.dist_id || "", q: "" }));
  }, [dispatch, talukFilters.dist_id]);

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((x) => x.value === sortBy)?.label || "Sort",
    [sortBy]
  );

  const distLabel = useMemo(() => {
    const selected = String(talukFilters.dist_id || "");
    if (!selected) return "All Districts";
    const d = (districts || []).find((x) => String(x.dist_id) === selected);
    return d?.dist_name || "Selected District";
  }, [talukFilters.dist_id, districts]);

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    let rows = Array.isArray(taluks) ? [...taluks] : [];

    if (q) {
      rows = rows.filter((r) =>
        `${r.taluk_name || ""} ${r.dist_name || ""}`.toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      const aTal = String(a.taluk_name || "");
      const bTal = String(b.taluk_name || "");
      const aDist = String(a.dist_name || "");
      const bDist = String(b.dist_name || "");
      const aSt = Number(a.status) === 1 ? 1 : 0;
      const bSt = Number(b.status) === 1 ? 1 : 0;

      switch (sortBy) {
        case "taluk_name_asc":
          return aTal.localeCompare(bTal);
        case "taluk_name_desc":
          return bTal.localeCompare(aTal);
        case "dist_name_asc":
          return aDist.localeCompare(bDist);
        case "dist_name_desc":
          return bDist.localeCompare(aDist);
        case "status_active_first":
          return bSt - aSt;
        case "status_inactive_first":
          return aSt - bSt;
        default:
          return aTal.localeCompare(bTal);
      }
    });

    return rows;
  }, [taluks, search, sortBy]);

  // pagination
  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, talukFilters.dist_id]);

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;
    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      e(
        Pagination.Prev,
        { key: "prev", onClick: () => goTo(currentPage - 1), disabled: currentPage === 1 },
        e(ChevronLeft, { size: 16, className: "me-1" }),
        "Prev"
      )
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(e(Pagination.Item, { key: 1, onClick: () => goTo(1) }, "1"));
      if (!isMobile && startPage > 2) items.push(e(Pagination.Ellipsis, { key: "es" }));
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        e(Pagination.Item, { key: i, active: i === currentPage, onClick: () => goTo(i) }, String(i))
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1) items.push(e(Pagination.Ellipsis, { key: "ee" }));
      items.push(
        e(Pagination.Item, { key: totalPages, onClick: () => goTo(totalPages) }, String(totalPages))
      );
    }

    items.push(
      e(
        Pagination.Next,
        { key: "next", onClick: () => goTo(currentPage + 1), disabled: currentPage === totalPages },
        "Next",
        e(ChevronRight, { size: 16, className: "ms-1" })
      )
    );

    return items;
  };

  const openAdd = () => {
    dispatch(resetDtForm());
    if (talukFilters.dist_id) dispatch(setDtForm({ dist_id: talukFilters.dist_id }));
    dispatch(openDtModal({ type: "taluk", mode: "add" }));
  };

  const openEdit = (r) => {
    dispatch(
      setDtForm({
        taluk_id: r.taluk_id,
        dist_id: r.dist_id,
        taluk_name: r.taluk_name,
        status: Number(r.status) === 1 ? 1 : 0,
      })
    );
    dispatch(openDtModal({ type: "taluk", mode: "edit" }));
  };

  const onSave = async () => {
    if (modalType !== "taluk") return;

    const payload = {
      taluk_id: form.taluk_id,
      dist_id: Number(form.dist_id || 0),
      taluk_name: String(form.taluk_name || "").trim(),
      status: Number(form.status) === 1 ? 1 : 0,
    };

    if (!payload.dist_id || !payload.taluk_name) {
      toast.warning("Please select District and enter Taluk name");
      return;
    }

    const res =
      modalMode === "add"
        ? await dispatch(createTalukThunk(payload))
        : await dispatch(updateTalukThunk(payload));

    if (res?.meta?.requestStatus === "fulfilled") {
      toast.success(modalMode === "add" ? "Taluk created ✅" : "Taluk updated ✅");
      dispatch(closeDtModal());
      dispatch(fetchTalukListThunk({ dist_id: talukFilters.dist_id || "" }));
    } else {
      toast.error(res?.payload || "Failed to save");
    }
  };

  const onDelete = (r) => {
    toast.info(
      ({ closeToast }) =>
        e(
          "div",
          null,
          e("div", { className: "fw-semibold mb-1" }, `Delete Taluk "${r.taluk_name}"?`),
          e(
            "div",
            { className: "d-flex gap-2 mt-2" },
            e(
              "button",
              {
                className: "btn btn-sm btn-danger",
                onClick: async () => {
                  const res = await dispatch(
                    deleteTalukThunk({ taluk_id: r.taluk_id, dist_id: r.dist_id })
                  );

                  if (res?.meta?.requestStatus === "fulfilled") {
                    toast.success("Taluk deleted");
                    dispatch(fetchTalukListThunk({ dist_id: r.dist_id }));
                  } else {
                    toast.error(res?.payload || "Failed to delete taluk");
                  }
                  closeToast();
                },
              },
              talukDeleting ? "..." : "Delete"
            ),
            e(
              "button",
              { className: "btn btn-sm btn-secondary", onClick: closeToast },
              "Cancel"
            )
          )
        ),
      { autoClose: false }
    );
  };

  const clearSearch = () => setSearch("");

  // ---------------------------
  // RENDER (NO JSX)
  // ---------------------------
  return e(
    "div",
    { className: "container-fluid px-3 py-4 mm-page" },

    // Header
    e(
      "div",
      { className: "d-flex justify-content-between align-items-center mb-3" },
      e(
        "div",
        null,
        e("h4", { className: "mb-1 fw-bold mm-title" }, "Taluk Master"),
        // e("div", { className: "mm-subtitle" }, "Create / Edit / Delete Taluks")
      ),
      e(
        "div",
        { className: "d-flex gap-2" },
        e(
          Button,
          {
            variant: "light",
            className: "mm-cancel-btn d-flex align-items-center gap-2",
            onClick: () => dispatch(fetchTalukListThunk({ dist_id: talukFilters.dist_id || "" })),
            disabled: talukLoading,
            title: "Refresh",
          },
          e(RefreshCcw, { size: 16 }),
          talukLoading ? "Refreshing..." : "Refresh"
        ),
        e(
          Button,
          {
            onClick: openAdd,
            className: "mm-primary-btn d-flex align-items-center gap-2",
            title: "Add Taluk",
          },
          e(Plus, { size: 18 }),
          "Add Taluk"
        )
      )
    ),

    // Error
    error
      ? e(
          Alert,
          { variant: "danger", className: "mm-card-soft mb-3" },
          e("div", { className: "fw-bold mb-1" }, "Error"),
          e("div", null, String(error))
        )
      : null,

    // Filters
    e(
      Card,
      { className: "mm-card-soft mb-3" },
      e(
        Card.Body,
        { className: "p-3" },
        e(
          Row,
          { className: "g-3 align-items-end" },

          // District filter
          e(
            Col,
            { md: 4 },
            e(Form.Label, { className: "fw-bold mb-1" }, "District"),
            e(
              Form.Select,
              {
                className: "mm-select",
                value: talukFilters.dist_id || "",
                onChange: (ev) => dispatch(setTalukFilters({ dist_id: ev.target.value })),
              },
              e("option", { value: "" }, "All Districts"),
              ...(districts || []).map((d) =>
                e("option", { key: d.dist_id, value: d.dist_id }, d.dist_name)
              )
            )
          ),

          // Search
          e(
            Col,
            { md: 5 },
            e(Form.Label, { className: "fw-bold mb-1" }, "Search"),
            e(
              InputGroup,
              null,
              e(
                InputGroup.Text,
                { className: "mm-inputgroup-text" },
                e(Search, { size: 18, className: "text-muted" })
              ),
              e(FormControl, {
                className: "mm-input mm-inputgroup-control",
                placeholder: "Search taluk / district...",
                value: search,
                onChange: (ev) => setSearch(ev.target.value),
              }),
              search
                ? e(
                    Button,
                    { variant: "light", className: "mm-clear-btn", onClick: clearSearch },
                    e(X, { size: 16 })
                  )
                : null
            )
          ),

          // Sort
          e(
            Col,
            { md: 3 },
            e(Form.Label, { className: "fw-bold mb-1" }, "Sort"),
            e(
              Dropdown,
              { align: "end" },
              e(
                Dropdown.Toggle,
                {
                  className:
                    "mm-dropdown-btn w-100 d-flex align-items-center justify-content-between",
                },
                e(
                  "span",
                  { className: "d-flex align-items-center gap-2" },
                  e(Filter, { size: 16 }),
                  sortLabel
                ),
                e(ChevronDown, { size: 16 })
              ),
              e(
                Dropdown.Menu,
                { className: "mm-dd-menu w-100" },
                ...SORT_OPTIONS.map((opt) =>
                  e(
                    Dropdown.Item,
                    {
                      key: opt.value,
                      active: sortBy === opt.value,
                      onClick: () => setSortBy(opt.value),
                    },
                    opt.label
                  )
                )
              )
            )
          ),

          // meta
          e(
            Col,
            { md: 12, className: "text-end" },
            e(
              "div",
              { className: "text-muted small" },
              "District: ",
              e("span", { className: "fw-bold" }, distLabel),
              " • Showing ",
              e(
                "span",
                { className: "fw-bold" },
                filteredRows.length === 0 ? 0 : startIndex + 1,
                "-",
                Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)
              ),
              " of ",
              e("span", { className: "fw-bold" }, filteredRows.length),
              " taluks"
            )
          )
        )
      )
    ),

    // Table
    e(
      Card,
      { className: "mm-card" },
      e(
        Card.Body,
        { className: "p-0" },
        talukLoading
          ? e(
              "div",
              { className: "text-center py-5" },
              e(Spinner, { animation: "border" }),
              e("div", { className: "mt-2 text-muted" }, "Loading taluks...")
            )
          : e(
              React.Fragment,
              null,
              e(
                "div",
                { className: "mm-table-wrap" },
                e(
                  Table,
                  { hover: true, className: "mm-table mb-0" },
                  e(
                    "thead",
                    null,
                    e(
                      "tr",
                      null,
                      e(
                        "th",
                        {
                          style: { cursor: "pointer", width: 280 },
                          onClick: () =>
                            setSortBy((p) =>
                              p === "taluk_name_asc" ? "taluk_name_desc" : "taluk_name_asc"
                            ),
                        },
                        e(
                          "div",
                          { className: "d-flex align-items-center gap-1" },
                          "Taluk ",
                          sortBy === "taluk_name_asc" ? e(ChevronUp, { size: 16 }) : null,
                          sortBy === "taluk_name_desc" ? e(ChevronDown, { size: 16 }) : null
                        )
                      ),
                      e(
                        "th",
                        {
                          style: { cursor: "pointer" },
                          onClick: () =>
                            setSortBy((p) =>
                              p === "dist_name_asc" ? "dist_name_desc" : "dist_name_asc"
                            ),
                        },
                        e(
                          "div",
                          { className: "d-flex align-items-center gap-1" },
                          "District ",
                          sortBy === "dist_name_asc" ? e(ChevronUp, { size: 16 }) : null,
                          sortBy === "dist_name_desc" ? e(ChevronDown, { size: 16 }) : null
                        )
                      ),
                      e("th", { style: { width: 160, textAlign: "center" } }, "Status"),
                      e(
                        "th",
                        { className: "mm-actions-th", style: { width: 180, textAlign: "right" } },
                        "Actions"
                      )
                    )
                  ),
                  e(
                    "tbody",
                    null,
                    ...(paginatedRows || []).map((r) =>
                      e(
                        "tr",
                        { key: r.taluk_id },
                        e(
                          "td",
                          {
                            className: "fw-bold",
                            style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                            title: r.taluk_name,
                          },
                          r.taluk_name
                        ),
                        e(
                          "td",
                          {
                            style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                            title: r.dist_name || "",
                          },
                          r.dist_name || "-"
                        ),
                        e("td", { className: "text-center" }, e(StatusPill, { status: r.status })),
                        e(
                          "td",
                          { className: "mm-actions-td", style: { textAlign: "right" } },
                          e(
                            "div",
                            { className: "d-inline-flex gap-2" },
                            e(
                              Button,
                              {
                                variant: "light",
                                className: "mm-icon-btn primary",
                                onClick: () => openEdit(r),
                                disabled: talukSaving,
                                title: "Edit",
                              },
                              e(Edit2, { size: 16 })
                            ),
                            e(
                              Button,
                              {
                                variant: "light",
                                className: "mm-icon-btn danger",
                                onClick: () => onDelete(r),
                                disabled: talukDeleting,
                                title: "Delete",
                              },
                              e(Trash2, { size: 16 })
                            )
                          )
                        )
                      )
                    ),
                    !paginatedRows?.length
                      ? e(
                          "tr",
                          null,
                          e(
                            "td",
                            { colSpan: 4, className: "p-5 text-center" },
                            e(
                              "div",
                              null,
                              e(
                                "div",
                                { className: "mm-empty-icon mx-auto mb-2" },
                                e(Search, { size: 22 })
                              ),
                              e(
                                "div",
                                { className: "fw-bold mb-1" },
                                search ? "No results found" : "No taluks found"
                              ),
                              e(
                                "div",
                                { className: "text-muted" },
                                search
                                  ? "No taluks match your search."
                                  : "Get started by adding your first taluk."
                              )
                            )
                          )
                        )
                      : null
                  )
                )
              ),

              totalPages > 1
                ? e(
                    "div",
                    { className: "d-flex justify-content-center py-3 border-top mm-footer" },
                    e(Pagination, { className: "mb-0 master-pagination" }, getPaginationItems())
                  )
                : null
            )
      )
    ),

    // Modal
    e(
      Modal,
      {
        show: !!(modalOpen && modalType === "taluk"),
        onHide: () => dispatch(closeDtModal()),
        centered: true,
        className: "mm-modal",
      },
      e(
        Form,
        { onSubmit: (ev) => (ev.preventDefault(), onSave()) },
        e(
          Modal.Header,
          { closeButton: true },
          e(Modal.Title, { className: "fw-bold" }, modalMode === "add" ? "Add Taluk" : "Edit Taluk")
        ),
        e(
          Modal.Body,
          null,
          e(
            Row,
            { className: "g-3" },
            e(
              Col,
              { md: 12 },
              e(Form.Label, { className: "fw-bold" }, "District *"),
              e(
                Form.Select,
                {
                  className: "mm-select",
                  value: form.dist_id || "",
                  onChange: (ev) => dispatch(setDtForm({ dist_id: ev.target.value })),
                },
                e("option", { value: "" }, "Select"),
                ...(districts || []).map((d) =>
                  e("option", { key: d.dist_id, value: d.dist_id }, d.dist_name)
                )
              )
            ),
            e(
              Col,
              { md: 12 },
              e(Form.Label, { className: "fw-bold" }, "Taluk Name *"),
              e(Form.Control, {
                className: "mm-input",
                value: form.taluk_name || "",
                onChange: (ev) => dispatch(setDtForm({ taluk_name: ev.target.value })),
                placeholder: "Ex: Kundapura",
              })
            ),
            e(
              Col,
              { md: 12 },
              e(Form.Label, { className: "fw-bold" }, "Status"),
              e(
                Form.Select,
                {
                  className: "mm-select",
                  value: Number(form.status) === 1 ? 1 : 0,
                  onChange: (ev) => dispatch(setDtForm({ status: Number(ev.target.value) })),
                },
                e("option", { value: 1 }, "Active"),
                e("option", { value: 0 }, "Inactive")
              )
            )
          )
        ),
        e(
          Modal.Footer,
          null,
          e(
            Button,
            {
              variant: "outline-secondary",
              className: "mm-cancel-btn",
              onClick: () => dispatch(closeDtModal()),
              disabled: talukSaving,
            },
            "Cancel"
          ),
          e(
            Button,
            {
              type: "submit",
              className: "mm-primary-btn",
              onClick: onSave,
              disabled: talukSaving,
            },
            talukSaving ? "Saving..." : "Save"
          )
        )
      )
    )
  );
}
